import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const ConfigurableReportBuilder = ({ onReportGenerate, savedReports = [] }) => {
  const [reportConfig, setReportConfig] = useState({
    name: '',
    description: '',
    metrics: [],
    groupBy: 'month',
    filters: {},
    schedule: 'manual',
    format: 'pdf',
    recipients: []
  });

  const [isBuilding, setIsBuilding] = useState(false);
  const [activeStep, setActiveStep] = useState(1);

  const availableMetrics = [
    { value: 'revenue', label: 'Receita Total', category: 'financial' },
    { value: 'expenses', label: 'Despesas Totais', category: 'financial' },
    { value: 'profit', label: 'Lucro Líquido', category: 'financial' },
    { value: 'profit_margin', label: 'Margem de Lucro', category: 'financial' },
    { value: 'customers', label: 'Total de Clientes', category: 'customer' },
    { value: 'new_customers', label: 'Novos Clientes', category: 'customer' },
    { value: 'customer_retention', label: 'Retenção de Clientes', category: 'customer' },
    { value: 'avg_order_value', label: 'Ticket Médio', category: 'sales' },
    { value: 'orders', label: 'Total de Pedidos', category: 'sales' },
    { value: 'conversion_rate', label: 'Taxa de Conversão', category: 'sales' },
    { value: 'inventory_turnover', label: 'Giro de Estoque', category: 'operations' },
    { value: 'efficiency', label: 'Eficiência Operacional', category: 'operations' },
    { value: 'market_share', label: 'Participação de Mercado', category: 'market' }
  ];

  const groupByOptions = [
    { value: 'day', label: 'Por Dia' },
    { value: 'week', label: 'Por Semana' },
    { value: 'month', label: 'Por Mês' },
    { value: 'quarter', label: 'Por Trimestre' },
    { value: 'year', label: 'Por Ano' },
    { value: 'product', label: 'Por Produto' },
    { value: 'customer_segment', label: 'Por Segmento de Cliente' },
    { value: 'channel', label: 'Por Canal de Venda' }
  ];

  const scheduleOptions = [
    { value: 'manual', label: 'Manual' },
    { value: 'daily', label: 'Diário' },
    { value: 'weekly', label: 'Semanal' },
    { value: 'monthly', label: 'Mensal' },
    { value: 'quarterly', label: 'Trimestral' }
  ];

  const formatOptions = [
    { value: 'pdf', label: 'PDF' },
    { value: 'excel', label: 'Excel' },
    { value: 'csv', label: 'CSV' },
    { value: 'powerpoint', label: 'PowerPoint' }
  ];

  const handleMetricToggle = (metricValue) => {
    setReportConfig(prev => ({
      ...prev,
      metrics: prev?.metrics?.includes(metricValue)
        ? prev?.metrics?.filter(m => m !== metricValue)
        : [...prev?.metrics, metricValue]
    }));
  };

  const handleConfigChange = (field, value) => {
    setReportConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const generateReport = async () => {
    setIsBuilding(true);
    
    // Simulate report generation
    setTimeout(() => {
      setIsBuilding(false);
      onReportGenerate?.(reportConfig);
      
      // Reset form
      setReportConfig({
        name: '',
        description: '',
        metrics: [],
        groupBy: 'month',
        filters: {},
        schedule: 'manual',
        format: 'pdf',
        recipients: []
      });
      setActiveStep(1);
    }, 3000);
  };

  const steps = [
    { number: 1, title: 'Configuração Básica', icon: 'Settings' },
    { number: 2, title: 'Métricas e Dados', icon: 'BarChart3' },
    { number: 3, title: 'Agendamento', icon: 'Calendar' },
    { number: 4, title: 'Revisão', icon: 'Eye' }
  ];

  const renderStepContent = () => {
    switch (activeStep) {
      case 1:
        return (
          <div className="space-y-4">
            <Input
              label="Nome do Relatório"
              placeholder="Ex: Relatório Executivo Mensal"
              value={reportConfig?.name}
              onChange={(e) => handleConfigChange('name', e?.target?.value)}
              required
            />
            <Input
              label="Descrição"
              placeholder="Descreva o objetivo deste relatório"
              value={reportConfig?.description}
              onChange={(e) => handleConfigChange('description', e?.target?.value)}
            />
            <Select
              label="Agrupar Dados Por"
              options={groupByOptions}
              value={reportConfig?.groupBy}
              onChange={(value) => handleConfigChange('groupBy', value)}
            />
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-body-medium text-card-foreground mb-3">
                Selecione as Métricas
              </label>
              
              {['financial', 'customer', 'sales', 'operations', 'market']?.map(category => (
                <div key={category} className="mb-4">
                  <h4 className="text-xs font-body-medium text-muted-foreground uppercase tracking-wide mb-2">
                    {category === 'financial' ? 'Financeiro' :
                     category === 'customer' ? 'Clientes' :
                     category === 'sales' ? 'Vendas' :
                     category === 'operations' ? 'Operações' : 'Mercado'}
                  </h4>
                  
                  <div className="grid grid-cols-1 gap-2">
                    {availableMetrics?.filter(metric => metric?.category === category)?.map(metric => (
                        <Checkbox
                          key={metric?.value}
                          label={metric?.label}
                          checked={reportConfig?.metrics?.includes(metric?.value)}
                          onChange={() => handleMetricToggle(metric?.value)}
                        />
                      ))
                    }
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <Select
              label="Frequência de Geração"
              options={scheduleOptions}
              value={reportConfig?.schedule}
              onChange={(value) => handleConfigChange('schedule', value)}
            />
            <Select
              label="Formato de Exportação"
              options={formatOptions}
              value={reportConfig?.format}
              onChange={(value) => handleConfigChange('format', value)}
            />
            <Input
              label="Destinatários (emails separados por vírgula)"
              placeholder="admin@dibrownie.com, gerente@dibrownie.com"
              value={reportConfig?.recipients?.join(', ')}
              onChange={(e) => handleConfigChange('recipients', e?.target?.value?.split(',')?.map(email => email?.trim()))}
            />
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div className="bg-muted rounded-lg p-4">
              <h4 className="font-body-medium text-sm text-card-foreground mb-3">
                Resumo da Configuração
              </h4>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Nome:</span>
                  <span className="font-body-medium">{reportConfig?.name || 'Não definido'}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Métricas:</span>
                  <span className="font-body-medium">{reportConfig?.metrics?.length} selecionadas</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Agrupamento:</span>
                  <span className="font-body-medium">
                    {groupByOptions?.find(opt => opt?.value === reportConfig?.groupBy)?.label}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Frequência:</span>
                  <span className="font-body-medium">
                    {scheduleOptions?.find(opt => opt?.value === reportConfig?.schedule)?.label}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Formato:</span>
                  <span className="font-body-medium">
                    {formatOptions?.find(opt => opt?.value === reportConfig?.format)?.label}
                  </span>
                </div>
              </div>
            </div>
            {reportConfig?.metrics?.length > 0 && (
              <div className="bg-muted rounded-lg p-4">
                <h4 className="font-body-medium text-sm text-card-foreground mb-2">
                  Métricas Selecionadas:
                </h4>
                <div className="flex flex-wrap gap-2">
                  {reportConfig?.metrics?.map(metricValue => {
                    const metric = availableMetrics?.find(m => m?.value === metricValue);
                    return (
                      <span key={metricValue} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                        {metric?.label}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border card-shadow">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading-semibold text-lg text-card-foreground">
            Construtor de Relatórios
          </h3>
          <Button
            variant="outline"
            size="sm"
            iconName="Save"
          >
            Salvar Template
          </Button>
        </div>

        {/* Step Navigation */}
        <div className="flex items-center space-x-4">
          {steps?.map((step, index) => (
            <React.Fragment key={step?.number}>
              <button
                onClick={() => setActiveStep(step?.number)}
                className={`
                  flex items-center space-x-2 px-3 py-2 rounded-lg transition-smooth
                  ${activeStep === step?.number 
                    ? 'bg-primary text-primary-foreground' 
                    : activeStep > step?.number
                      ? 'bg-success/10 text-success' :'bg-muted text-muted-foreground hover:bg-accent'
                  }
                `}
              >
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-current/20">
                  {activeStep > step?.number ? (
                    <Icon name="Check" size={14} />
                  ) : (
                    <Icon name={step?.icon} size={14} />
                  )}
                </div>
                <span className="text-sm font-body-medium hidden sm:block">
                  {step?.title}
                </span>
              </button>
              
              {index < steps?.length - 1 && (
                <div className={`w-8 h-0.5 ${activeStep > step?.number ? 'bg-success' : 'bg-border'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className="p-6">
        {renderStepContent()}
      </div>
      <div className="p-6 border-t border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {activeStep > 1 && (
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronLeft"
                onClick={() => setActiveStep(prev => Math.max(1, prev - 1))}
              >
                Anterior
              </Button>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {activeStep < 4 ? (
              <Button
                variant="default"
                size="sm"
                iconName="ChevronRight"
                iconPosition="right"
                onClick={() => setActiveStep(prev => Math.min(4, prev + 1))}
                disabled={activeStep === 1 && !reportConfig?.name}
              >
                Próximo
              </Button>
            ) : (
              <Button
                variant="default"
                size="sm"
                iconName="Download"
                loading={isBuilding}
                onClick={generateReport}
                disabled={reportConfig?.metrics?.length === 0}
              >
                {isBuilding ? 'Gerando...' : 'Gerar Relatório'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigurableReportBuilder;